import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import GridOnIcon from '@mui/icons-material/GridOn';
import {
  normalizePostProcessOrder,
  orderedPipelineSteps,
  pipelineStepInputLabel,
  resolveChartFeedFromPipeline,
} from '../../utils/chartFeedMeta';

const STEP_ICONS = {
  episodes: TimelineIcon,
  zones: GridOnIcon,
};

function FlowArrow() {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ userSelect: 'none' }}>
      →
    </Typography>
  );
}

export default function ChartFeedBar({
  postProcessOrder,
  episodeAnalysis,
  spatialZoneAnalysis,
  onPostProcessOrderChange,
}) {
  const episodesEnabled = Boolean(episodeAnalysis?.enabled);
  const zonesEnabled = Boolean(spatialZoneAnalysis?.enabled);
  const order = normalizePostProcessOrder(postProcessOrder);
  const steps = orderedPipelineSteps(order, episodesEnabled, zonesEnabled);
  const chartFeed = resolveChartFeedFromPipeline(order, episodesEnabled, zonesEnabled);
  const showPipeline = steps.length > 0;

  const pipelineLabel = (step) => (step === 'episodes' ? 'Episodios' : 'Zonas');

  const renderStepChip = (step) => {
    const feedsChart = step === chartFeed;
    const StepIcon = STEP_ICONS[step] || TableChartIcon;
    const inputLabel = pipelineStepInputLabel(step, order, episodesEnabled, zonesEnabled);
    return (
      <Tooltip
        key={step}
        title={`${inputLabel} → ${pipelineLabel(step)}${feedsChart ? ' → Gráfico' : ''}`}
      >
        <Chip
          size="small"
          icon={<StepIcon fontSize="small" />}
          label={pipelineLabel(step)}
          color={feedsChart ? 'primary' : 'default'}
          variant={feedsChart ? 'filled' : 'outlined'}
        />
      </Tooltip>
    );
  };

  if (!showPipeline) {
    return (
      <Paper variant="outlined" sx={{ p: 1, mb: 0.5, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Pipeline
          </Typography>
          <Chip
            icon={<TableChartIcon fontSize="small" />}
            label="Serie SQL"
            size="small"
            variant="outlined"
          />
          <FlowArrow />
          <Chip
            icon={<ShowChartIcon fontSize="small" />}
            label="Gráfico"
            size="small"
            color="primary"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
          <Typography variant="caption" color="text.secondary">
            Activa Episodios o Zonas para encadenar tablas. El paso más a la derecha antes del gráfico alimenta la vista.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        mb: 0.5,
        bgcolor: 'primary.50',
        borderColor: 'primary.light',
      }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Pipeline
        </Typography>
        <Chip size="small" label="Serie SQL" variant="outlined" icon={<TableChartIcon fontSize="small" />} />
        {steps.map((step) => (
          <React.Fragment key={step}>
            <FlowArrow />
            {renderStepChip(step)}
          </React.Fragment>
        ))}
        <FlowArrow />
        <Chip
          size="small"
          icon={<ShowChartIcon fontSize="small" />}
          label="Gráfico"
          color="primary"
          variant="filled"
          sx={{ fontWeight: 600 }}
        />
        {steps.length > 1 && (
          <Tooltip title="Intercambiar orden de Episodios y Zonas">
            <IconButton
              size="small"
              onClick={() => onPostProcessOrderChange?.([order[1], order[0]])}
              aria-label="Intercambiar orden del pipeline"
            >
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}
